<template>
  <div>
    <b-dropdown
      left
      :data-test="`dropdown-button-${index}`"
      variant="plain"
      toggle-class="text-decoration-none"
      no-caret
    >
      <template v-slot:button-content>
        <i class="fas fa-ellipsis-v" />
      </template>
      <b-dropdown-item
        :data-test="`reporting-link-${index}`"
        :to="{ name: 'ReportingIndex',
               params: {
                 campaignId: item.id,
                 advertiserId: item.advertiserId,
               }
        }"
        :disabled="isReportDisabled"
      >
        View Reporting
      </b-dropdown-item>
      <feature
        name="CAMPAIGN_COPY"
      >
        <div v-if="isMyDashboard">
          <b-dropdown-divider />
          <b-dropdown-item
            @click="onCopyCampaignClick"
            :data-test="`copy-campaign-${index}`"
          >
            Copy Campaign
          </b-dropdown-item>
        </div>
      </feature>
      <feature
        name="MANAGED_CAMPAIGNS"
      >
        <div v-if="isInReview && isMyDashboard">
          <b-dropdown-divider />
          <b-dropdown-item
            @click="onSetLiveCampaignClick"
            :data-test="`set-live-campaign-${index}`"
          >
            Set Campaign {{ getCampaignLiveOrPendingState }}
          </b-dropdown-item>
        </div>
      </feature>
      <div v-if="isInReview && !isMyDashboard">
        <b-dropdown-divider />
        <b-dropdown-item
          @click="onSetSelfServiceCampaignLiveClick"
          :data-test="`set-live-campaign-${index}`"
        >
          Set Campaign {{ getCampaignLiveOrPendingState }}
        </b-dropdown-item>
      </div>
      <feature
        name="MANAGED_CAMPAIGNS"
      >
        <div v-if="isInReview">
          <b-dropdown-divider />
          <b-dropdown-item
            @click="setCampaignToDraft"
            :data-test="`set-campaign-draft-${index}`"
          >
            Set Campaign to DRAFT
          </b-dropdown-item>
        </div>
      </feature>
      <div v-if="isMyDashboard">
        <b-dropdown-divider />
        <b-dropdown-item
          @click="onDeleteCampaignClick"
          :disabled="isDeleteDisabled"
          variant="danger"
          :data-test="`delete-campaign-${index}`"
        >
          Delete Campaign
        </b-dropdown-item>
      </div>

      <feature
        name="MANAGED_CAMPAIGNS"
      >
        <b-dropdown-divider />
        <b-dropdown-item
          :data-test="`campaign-link-${index}`"
          target="_blank"
          :href="getCampaignLink"
        >
          View in SMAdmin
        </b-dropdown-item>
      </feature>
    </b-dropdown>

    <b-modal
      :id="`CopyCampaignModal-${item.id}`"
      :title="`Copying ${item.name}`"
      data-test="copy-campaign-modal"
      hide-footer
      hide-backdrop
      @shown="shownModal"
    >
      <div class="d-block">
        <b-form-group
          id="fieldset-new-campaign-name"
          label="New campaign name:"
          label-for="new-campaign-name-input"
          label-class="form-label"
          :state="newCampaignNameState"
          :invalid-feedback="invalidFeedback"
        >
          <b-form-input
            id="new-campaign-name-input"
            data-test="new-campaign-input"
            v-model="campaignName"
            size="lg"
            :state="newCampaignNameState"
            trim
          />
          <b-form-checkbox
            id="copyTrackers"
            class="my-4"
            v-model="copyTrackers"
            name="copyTrackers"
            :value="true"
            :unchecked-value="false"
          >
            Copy Trackers
          </b-form-checkbox>
        </b-form-group>
        <!-- <b-alert
          show
          variant="warning"
        >
          Warning - trackers will not be copied to the new campaign!
        </b-alert> -->
      </div>
      <b-button
        class="mt-3"
        :class="{ 'btn-loading': isLoading }"
        data-test="copy-campaign-button"
        variant="primary"
        block
        @click="copyCampaign"
        :disabled="!newCampaignNameState || isLoading"
      >
        Copy your campaign
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import helper from '@/core/helper';

export default {
  name: 'CampaignActions',
  props: {
    index: {
      type: Number,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    showEnded: {
      type: Boolean,
      default: false,
    },
    isMyDashboard: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      clicked: false,
      isLoading: false,
      campaignName: '',
      checkNameExists: false,
      copyTrackers: true,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaigns',
    ]),
    getCampaignLink() {
      return `${process.env.VUE_APP_NAV_CAMPAIGNS_LINK}/campaign/${this.item.id}`;
    },
    newCampaignNameState() {
      if (this.checkNameExists) {
        return false;
      }
      if (this.campaignName.length > 4) {
        return true;
      }
      return false;
    },
    invalidFeedback() {
      if (this.checkNameExists) {
        return 'This name is already being used for another campaign';
      }
      if (this.campaignName.length > 0) {
        return 'Enter at least 4 characters';
      }
      return 'Please enter something';
    },
    isReportDisabled() {
      return !this.item.state || this.item.state.toLowerCase() === 'draft' || this.isInReview;
    },
    isDeleteDisabled() {
      return !this.item.state || this.item.state.toLowerCase() !== 'draft';
    },
    isInReview() {
      return this.item.state.toLowerCase() === 'review';
    },
    getCampaignLiveOrPendingState() {
      const today = new Date();
      const startDate = new Date(this.item.startDate * 1000);
      return startDate > today ? 'Pending' : 'Live';
    },
  },
  methods: {
    ...mapActions('campaigns', [
      types.COPY_CAMPAIGN,
      types.SET_CAMPAIGN_LIVE,
      types.SET_CAMPAIGN_STATE,
      types.GET_CAMPAIGN_BY_NAME,
    ]),
    onDeleteCampaignClick() {
      this.$emit('removeCampaign', this.item);
    },
    onCopyCampaignClick() {
      this.$root.$emit('bv::show::modal', `CopyCampaignModal-${this.item.id}`);
    },
    onSetLiveCampaignClick() {
      this.$emit('startCampaign', this.item);
    },
    onSetSelfServiceCampaignLiveClick() {
      this.SET_CAMPAIGN_LIVE(this.item.id);
    },
    setCampaignToDraft() {
      this.$emit('setCampaignToDraft', this.item);
    },
    checkCampaignName: helper.debounce(function checkCampaignName() {
      if (this.campaignName !== '') {
        this.GET_CAMPAIGN_BY_NAME(this.campaignName)
          .then(({ status }) => {
            if (status !== 200) {
              this.checkNameExists = false;
              return;
            }
            this.checkNameExists = true;
          });
      }
      this.checkNameExists = false;
    }, 1000),
    copyCampaign() {
      if (this.clicked) return;
      this.clicked = true;
      this.isLoading = true;
      const payload = {
        campaignId: this.item.id,
        advertiserId: this.item.advertiserId,
        toCampaignName: this.campaignName,
        copyTrackers: this.copyTrackers,
      };
      this.COPY_CAMPAIGN(payload)
        .then((campaign) => {
          this.clicked = false;
          this.isLoading = false;
          this.$root.$emit('bv::hide::modal', `CopyCampaignModal-${this.item.id}`);
          this.$router.push(`/new-campaign/aid/${campaign.advertiserId}/cid/${campaign.id}`);
        }).catch(() => {
          this.clicked = false;
          this.isLoading = false;
        });
    },
    shownModal() {
      this.campaignName = `(Copy) ${this.item.name}`;
    },
  },
  watch: {
    campaignName() {
      this.checkCampaignName();
    },
  },
};
</script>
